/**
 * @typedef {Object} PlanDetails
 * @property {string} code
 * @property {number} monthlyCost
 * @property {number} [annualCost]
 * @property {number} setupFee
 * @property {string} customerType
 * @property {string} planName
 * @property {string} capacityLabel
 * @property {number} capacityAmount
 * @property {string} [secondaryCapacityLabel]
 * @property {number} [secondaryCapacityAmount]
 */

export const CUSTOMER_TYPES = {
  LENDER: 'Lender',
  REAL_ESTATE_AGENT: 'Real Estate Agent',
  INSURANCE_AGENT: 'Insurance Agent',
  TITLE_AGENT: 'Title Agent',
};

export const PLAN_NAMES = {
  STARTER: 'Starter',
  INDIVIDUAL: 'Individual',
  PARTNER: 'Partner',
  TEAMS: 'Teams',
  FREEMIUM_STARTER: 'Freemium Starter',
  PRO: 'Pro',
  UNLIMITED: 'Unlimited'
};

/** @type {Object.<string, PlanDetails>} */
const planData = {
  'hb-lender-starter': {
    code: 'hb-lender-starter',
    monthlyCost: 125,
    annualCost: 1250,
    setupFee: 100,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: PLAN_NAMES.STARTER,
    capacityLabel: 'Homeowners',
    capacityAmount: 100,
    secondaryCapacityLabel: 'buyers',
    secondaryCapacityAmount: 100
  },
  'hb-lender-pro': {
    code: 'hb-lender-pro',
    monthlyCost: 225,
    annualCost: 2250,
    setupFee: 100,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: PLAN_NAMES.INDIVIDUAL,
    capacityLabel: 'Homeowners',
    capacityAmount: 500,
    secondaryCapacityLabel: 'buyers',
    secondaryCapacityAmount: 1500
  },
  'hb-lender-unlimited': {
    code: 'hb-lender-unlimited',
    monthlyCost: 300,
    annualCost: 3000,
    setupFee: 100,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: PLAN_NAMES.UNLIMITED,
    capacityLabel: 'Active Portal Users',
    capacityAmount: 500,
    secondaryCapacityLabel: 'clients',
    secondaryCapacityAmount: 'Unlimited'
  },
  'hb-agent-solo': {
    code: 'hb-agent-solo',
    monthlyCost: 50,
    setupFee: 50,
    customerType: CUSTOMER_TYPES.REAL_ESTATE_AGENT,
    planName: PLAN_NAMES.STARTER,
    capacityLabel: 'homebuyers',
    capacityAmount: 250
  },
  'hb-agent-partner': {
    code: 'hb-agent-partner',
    monthlyCost: 25,
    setupFee: 50,
    customerType: CUSTOMER_TYPES.REAL_ESTATE_AGENT,
    planName: PLAN_NAMES.PARTNER,
    capacityLabel: 'homebuyers',
    capacityAmount: 250
  },
  'hb-agent-team': {
    code: 'hb-agent-team',
    monthlyCost: 100,
    setupFee: 50,
    customerType: CUSTOMER_TYPES.REAL_ESTATE_AGENT,
    planName: PLAN_NAMES.TEAMS,
    capacityLabel: 'homebuyers',
    capacityAmount: 250
  },
  'freemium-starter': {
    code: 'freemium-starter',
    monthlyCost: 0,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.REAL_ESTATE_AGENT,
    planName: PLAN_NAMES.FREEMIUM_STARTER,
    capacityLabel: 'homebuyers',
    capacityAmount: 25
  },
  'hb-insurance-agent-pro': {
    code: 'hb-insurance-agent-pro',
    monthlyCost: 200,
    setupFee: 200,
    customerType: CUSTOMER_TYPES.INSURANCE_AGENT,
    planName: PLAN_NAMES.PRO,
    capacityLabel: 'contacts',
    capacityAmount: 2000
  },
  'hb-title-agent-pro': {
    code: 'hb-title-agent-pro',
    monthlyCost: 200,
    setupFee: 200,
    customerType: CUSTOMER_TYPES.TITLE_AGENT,
    planName: PLAN_NAMES.PRO,
    capacityLabel: 'contacts',
    capacityAmount: 500
  },
  'fairway-unlimited-pro': {
    code: 'fairway-unlimited-pro',
    monthlyCost: 180,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Fairway Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'fairway-unlimited-starter': {
    code: 'fairway-unlimited-starter',
    monthlyCost: 135,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Fairway Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      'Unlimited Clients & Email Engagement',
      '100 Actively Engaged Clients'
    ]
  },
  'edge-preferred-broker-pro': {
    code: 'edge-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Edge Lender Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'edge-preferred-broker-starter': {
    code: 'edge-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Edge Lender Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'west-preferred-broker-pro': {
    code: 'west-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'West Capital Lender Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'west-preferred-broker-starter': {
    code: 'west-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'West Capital Lender Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'union-preferred-broker-pro': {
    code: 'union-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Union Preferred Broker Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'union-preferred-broker-starter': {
    code: 'union-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Union Preferred Broker Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'rocket-preferred-broker-pro': {
    code: 'rocket-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Rocket Preferred Broker Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'rocket-preferred-broker-starter': {
    code: 'rocket-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Rocket Preferred Broker Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'barrett-preferred-broker-pro': {
    code: 'barrett-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Barrett Preferred Broker Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'barrett-preferred-broker-starter': {
    code: 'barrett-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Barrett Preferred Broker Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'arbor-financial-group-preferred-broker-pro': {
    code: 'arbor-financial-group-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Arbor Financial Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'arbor-financial-group-preferred-broker-starter': {
    code: 'arbor-financial-group-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'Arbor Financial Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  },
  'c2-preferred-broker-pro': {
    code: 'c2-preferred-broker-pro',
    monthlyCost: 205,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'C2 Preferred Broker Pro',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 500,
    features: [
      'Unlimited Clients & Email Engagement',
      '500 Actively Engaged Clients'
    ]
  },
  'c2-preferred-broker-starter': {
    code: 'c2-preferred-broker-starter',
    monthlyCost: 125,
    setupFee: 0,
    customerType: CUSTOMER_TYPES.LENDER,
    planName: 'C2 Preferred Broker Starter',
    capacityLabel: 'Actively Engaged Clients',
    capacityAmount: 100,
    features: [
      '100 Clients and 100 Prospects',
      '$25/mo for each additional 100 Clients'
    ]
  }
};

/**
 * Mapping of corporate profile IDs to plan codes
 * Can be a single plan code (string) or multiple plan codes (array)
 * @type {Object.<string, string|string[]>}
 */
export const companyIdToPlanMap = {
  // '00810f49-666e-4294-8a8c-9a9feec5c622': ['fairway-unlimited-pro', 'fairway-unlimited-starter'], // swap to local company id for testing
  '218d90e3-3f41-4000-b6ae-467edfc4e929': ['fairway-unlimited-pro', 'fairway-unlimited-starter'],
  '9037c833-17c2-43fe-aec3-ae4c43cecf6c': ['edge-preferred-broker-pro', 'edge-preferred-broker-starter'],
  '65d85af7-bdce-485e-b60a-d290bf090807': ['west-preferred-broker-pro', 'west-preferred-broker-starter'],
  '32b78cdb-ad7c-4a35-b2b4-b8a41cebf41e': ['union-preferred-broker-pro', 'union-preferred-broker-starter'],
  'b92d17aa-68dd-49c9-8097-583926507d62': ['rocket-preferred-broker-pro', 'rocket-preferred-broker-starter'],
  'bd5ca4d1-858d-4535-abb8-89e1164e0344': ['barrett-preferred-broker-pro', 'barrett-preferred-broker-starter'],
  'e1e7c655-735a-4b71-ad70-f1e1a7886967': ['arbor-financial-group-preferred-broker-pro', 'arbor-financial-group-preferred-broker-starter'],
  '14b5e174-cb5d-4334-957e-e0f32e97a93a': ['c2-preferred-broker-pro', 'c2-preferred-broker-starter']
};

/**
 * @param {string} planCode
 * @returns {PlanDetails|null}
 */
export const getPlanDetails = (planCode) => {
  return planData[planCode] || null;
};

/**
 * Get plan code(s) for a given company ID
 * @param {string} companyId
 * @returns {string[]|null} - Always returns an array of plan codes or null
 */
export const getPlanCodesForCompany = (companyId) => {
  const plans = companyIdToPlanMap[companyId];
  if (!plans) return null;
  // Always return as array for consistency
  return Array.isArray(plans) ? plans : [plans];
};

export const plans = Object.values(planData);
